import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import bannerImg from "../../../assets/landing-page/alysei-bg.png";
import useLocale from "../../../components/Hooks/useLocale";
import { urlWithLocalization } from "../../../components/LocalizationUrl/UrlWithLocalization";
import language from "../../../helpers/language";

const   NewBanner = () => {
  const history = useHistory();
  const [lang] = useState(language.getLang());
  const localeLocalization = useLocale();
  const [search, setSearch] = useState("");
  const [refine, setRefine] = useState("");
  const aiAssistantPlaceholders = useMemo(
    () => lang?.aiAssistantPlaceholders || ["What are you looking for today?"],
    [lang]  
  );
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  const [isDeletingPlaceholder, setIsDeletingPlaceholder] = useState(false);

  useEffect(() => {
    const currentText = aiAssistantPlaceholders[placeholderIndex] || "";

    if (!isDeletingPlaceholder && typedPlaceholder.length === currentText.length) {
      const pauseTimer = setTimeout(() => setIsDeletingPlaceholder(true), 1200);
      return () => clearTimeout(pauseTimer);
    }

    if (isDeletingPlaceholder && typedPlaceholder.length === 0) {
      setIsDeletingPlaceholder(false);
      setPlaceholderIndex((currentIndex) => (currentIndex + 1) % aiAssistantPlaceholders.length);
      return undefined;
    }

    const typeTimer = setTimeout(() => {
      const nextLength = typedPlaceholder.length + (isDeletingPlaceholder ? -1 : 1);
      setTypedPlaceholder(currentText.slice(0, nextLength));
    }, isDeletingPlaceholder ? 24 : 45);

    return () => clearTimeout(typeTimer);
  }, [aiAssistantPlaceholders, isDeletingPlaceholder, placeholderIndex, typedPlaceholder]);

  const focusAssistantSearch = () => {
    const input = document.getElementById("ai-assistant-search-input");
    input?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => input?.focus(), 450);
  };

  return (
    <div className="relative min-h-screen flex flex-col justify-center pt-20">
      {/* Background image */}
      <img
        src={bannerImg}
        alt="Italian landscape"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-5 md:px-16 py-12">
        {/* Welcome pill */}
        <span className="inline-block bg-white/15 text-white text-xs font-medium px-4 py-1.5 rounded-full mb-6 backdrop-blur-md border border-white/30">
          {lang?.Welcome}
        </span>

        {/* Headline */}
        <h1 className="text-white text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight max-w-4xl mb-5">
          {lang?.SourcePremium}
        </h1>

        {/* Sub-headline */}
        <p className="text-white/90 text-sm md:text-base max-w-2xl mb-7">
          {lang?.GetMatched}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4 justify-center mb-10">
          <button
            type="button"
            onClick={focusAssistantSearch}
            className="px-7 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-md transition-colors text-sm shadow-md"
          >
           {lang?.findSuppliers}
          </button>
          <Link
            to={urlWithLocalization(localeLocalization, "/register/italian-food-beverage-producers")}
            className="px-7 py-3 bg-royal hover:opacity-90 text-white font-semibold rounded-md transition-opacity text-sm shadow-md"
          >
            {lang?.join_as_seller}
          </Link>
        </div>

        {/* Search Card */}
        <div className="w-full max-w-2xl">
          <p className="text-white text-base font-semibold mb-3">
            Tell us what you are looking for
          </p>
          <p className="text-white/80 text-xs mb-3">
            Describe your sourcing needs and we'll match you with relevant Italian producers.
          </p>

          <div id="ai-assistant-search" className="bg-white rounded-2xl shadow-2xl p-4 text-left">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="relative">
                  {!search && (
                    <div className="absolute inset-0 flex items-center pointer-events-none text-sm text-gray-400">
                      <span>{typedPlaceholder}</span>
                      <span className="inline-block w-[1px] h-4 bg-gray-400 ml-0.5 animate-pulse" />
                    </div>
                  )}
                <input
                  id="ai-assistant-search-input"
                  type="text"
                  placeholder=""
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.trim()) {
                      history.push("/chat", { initialMessage: search.trim() });
                    }
                  }}
                    className="w-full text-sm text-gray-800 placeholder-gray-400 outline-none bg-transparent relative z-10"
                />
                </div>
              </div>
              <button
                onClick={() => {
                  if (search.trim()) {
                    history.push("/chat", { initialMessage: search.trim() });
                  }
                }}
                className="w-10 h-10 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center transition-colors flex-shrink-0"
              >
                <i className="fa fa-arrow-up text-sm" />
              </button>
            </div>
            <div className="border-t border-gray-100 mt-3 pt-3 flex items-center gap-2">
              <i className="fa fa-sliders-h text-gray-400 text-xs" />
              <input
                type="text"
                placeholder="Refine your request (optional)"
                value={refine}
                onChange={(e) => setRefine(e.target.value)}
                className="flex-1 text-xs text-gray-700 placeholder-gray-400 outline-none"
              />
            </div>
          </div>

          <p className="text-white/90 text-xs mt-3">
            We'll match you with 3–5 relevant producers!
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewBanner;
